<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { useStore } from 'vuex';
  import { ColumnConfigurationInterface } from '@/shared/columnConfiguration.interface';
  import {
    FilterParams,
    SearchParamsAND,
    AdditionalParams,
  } from '@/shared/filterParams';
  import { tableConfigurations } from '@/shared/tableConfigurations';
  import Resource from '@/models/resource/resource.model';
  import IconAtom from '@/components/atoms/IconAtom.vue';
  import { useRoute, useRouter } from 'vue-router';
  import NoDataMolecule from '../no-data-molecule/NoDataMolecule.vue';
  import { NoDataStateProps } from '@/shared/noDataStateProps';
  import SearchbarMolecule from '../searchbar-molecule/SearchbarMolecule.vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import TabbarOrganism from '@/components/organisms/tabbar-organism/TabbarOrganism.vue';
  import userDefaultImage from '@/assets/default-user-img.png';
  import defaultImage from '@/assets/default-image.png';
  import UserActionModal from '../user-action-modal/UserActionModal.vue';
  import ViewCommentsMolecule from '@/components/molecules/view-comments-molecule/ViewCommentsMolecule.vue';
  import NotificationActionModal from '../notification-action-modal/NotificationActionModal.vue';
  import userService from '@/services/user/user.service';
  const emits = defineEmits(['update:selectedRows', 'navigateToParent']);

  const props = defineProps<{
    dataType: string;
    filterOptions?: any[];
    dynamicDefaultFilter?: SearchParamsAND[];
    dynamicAdditionalFilter?: AdditionalParams;
    onActionClick?: () => void;
    type?: 'resource' | 'GroupDiscussion' | 'parent_comment';
    currentId?: string;
  }>();
  const route = useRoute();
  const router = useRouter();

  const store = useStore();
  const currentPage = ref(1);
  const userActionModal = ref(false);
  const notificationActionModal = ref(false);
  const selectedId = ref<string>('');
  const selectedDataType = ref<string>('');
  const activeButton = ref<string>('');
  const showNoCommentsModal = ref(false);
  const filterParams = ref(
    new FilterParams(
      1,
      10,
      { searchTerm: '' },
      tableConfigurations[props.dataType]?.defaultFilters ??
        props.dynamicDefaultFilter ??
        [],
      props.dynamicAdditionalFilter || undefined,
      { sortBy: 'created_at', sortOrder: 'desc' },
    ),
  );
  const statusBarSelection = ref();
  const selectedOptions = ref<string[]>([]);

  const showResourcesModal = ref(false);

  const actionButton = ref<any>(null);

  const tableActionButtons = () => {
    const buttonCount =
      tableConfigurations[props.dataType]?.tableActionButtonProps?.length ?? 0;
    const buttons =
      tableConfigurations[props.dataType]?.tableActionButtonProps ?? [];

    if (buttonCount > 0) {
      if (buttonCount == 1 && selectedOptions.value.length == 0) {
        actionButton.value = null;
      } else if (selectedOptions.value.length > 0 && buttonCount > 1) {
        actionButton.value = buttons[1];
      } else {
        actionButton.value = buttons[0];
      }
    } else {
      actionButton.value = null;
    }
  };

  const columnConfiguration = ref<ColumnConfigurationInterface[]>(
    tableConfigurations[props.dataType].columns,
  );

  const noDataStateProps = computed<NoDataStateProps>(() => {
    return tableConfigurations[props.dataType].noDataStateProps;
  });

  const rowsData = computed(() => {
    return store.getters[
      `${tableConfigurations[props.dataType].storeActions.items}`
    ];
  });

  const totalPages = computed(() => {
    return store.getters[
      `${tableConfigurations[props.dataType].storeActions.totalPages}`
    ];
  });

  let isUpdating = false;

  watch(selectedOptions, (newVal) => {
    tableActionButtons();
  });

  function getFilterParams(): FilterParams {
    const newFilterParams = structuredClone(filterParams.value);
    newFilterParams.searchParamsAND = newFilterParams.searchParamsAND?.filter(
      (item) => {
        return (
          item.searchField != tableConfigurations[props.dataType].tabs.field
        );
      },
    );

    if (statusBarSelection.value && statusBarSelection.value.searchTerm != '') {
      newFilterParams.searchParamsAND?.push(statusBarSelection.value);
    }

    newFilterParams.searchParamsAND = [
      ...(newFilterParams.searchParamsAND || []),
      ...(tableConfigurations[props.dataType]?.defaultFilters || []),
      ...(props.dynamicDefaultFilter || []),
    ];

    if (props.dynamicAdditionalFilter) {
      newFilterParams.additionalParams = props.dynamicAdditionalFilter;
    }

    if (statusBarSelection.value && statusBarSelection.value.searchTerm != '') {
      if (newFilterParams.searchParamsAND) {
        newFilterParams.searchParamsAND?.push(statusBarSelection.value);
      } else {
        newFilterParams.searchParamsAND = [statusBarSelection.value];
      }
    }

    return newFilterParams;
  }

  const createActionButtuon = (
    buttonName: string,
    dataModel: string,
    actionName: string,
  ) => {
    columnConfiguration.value = [
      ...tableConfigurations[props.dataType].columns,
      {
        actionName: buttonName,
        actionHandler: async (data: any) => {
          await store.dispatch(dataModel + actionName, {
            id: data[`${dataModel}_id`],
            filters: { ...filterParams.value },
          });
        },
      },
    ];
  };

  const openCommentsModal = (
    dataType: string,
    id: string,
    isCommentColumn: boolean,
  ) => {
    if (isCommentColumn) {
      selectedDataType.value = dataType;
      selectedId.value = id;
      showNoCommentsModal.value = true;
    }
  };

  const openDiscussionModal = () => {
    store.commit('group/SHOW_DISCUSSION_MODAL', true);
  };

  watch(
    () => props.dynamicDefaultFilter,
    (newVal) => {
      if (isUpdating) return;
      const newFilterParams = getFilterParams();

      isUpdating = true;
      currentPage.value = 1;
      fetchData(newFilterParams).finally(() => {
        isUpdating = false;
      });
    },
    { deep: true },
  );

  watch(
    filterParams,
    (newVal) => {
      if (isUpdating) return;
      const newFilterParams = getFilterParams();

      isUpdating = true;
      currentPage.value = newFilterParams.page;
      fetchData(newFilterParams).finally(() => {
        isUpdating = false;
      });
    },
    { deep: true },
  );

  watch(statusBarSelection, (newVal) => {
    if (
      [
        'Article',
        'Video',
        'Audio',
        'Group',
        'User',
        'Collection',
        'Discussion',
      ].includes(props.dataType)
    ) {
      const dataModel = ['Article', 'Video', 'Audio'].includes(props.dataType)
        ? 'resource'
        : props.dataType.toLowerCase();

      switch (statusBarSelection.value.searchTerm) {
        case 'draft':
          createActionButtuon('Publish', dataModel, '/publishItem');
          break;
        case 'unpublished':
          createActionButtuon('Restore', dataModel, '/restoreItem');
          break;
        case 'banned':
          if (props.dataType === 'User') {
            columnConfiguration.value = [
              ...tableConfigurations[props.dataType].columns,
            ];
          } else {
            createActionButtuon('Restore', dataModel, '/restoreItem');
          }
          break;
        case 'inactive':
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns.filter(
              (column) => column.title != 'Actions',
            ),
          ];
          break;
        default:
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns,
          ];
          break;
      }
    }
    if (props.dataType == 'Notification') {
      switch (statusBarSelection.value.searchTerm) {
        case 'sent':
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns.filter(
              (column) =>
                column.title != 'Scheduled' &&
                column.title != 'Repeat' &&
                column.title != 'Last Modified',
            ),
          ];
          break;
        case 'draft':
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns.filter(
              (column) =>
                column.title != 'Scheduled' &&
                column.title != 'Repeat' &&
                column.title != 'Time Sent' &&
                column.title != 'Last Modified',
            ),
          ];
          break;
        case 'scheduled':
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns.filter(
              (column) => column.title != 'Time Sent',
            ),
          ];
          break;
        default:
          columnConfiguration.value = [
            ...tableConfigurations[props.dataType].columns,
          ];
          break;
      }
    }

    const newFilterParams = filterParams.value;
    newFilterParams.page = 1;
    filterParams.value = { ...newFilterParams };
  });

  const deleteComment = (comment_id, event) => {
    event.stopPropagation();

    store.dispatch('comment/deleteItems', { data: [comment_id] });
    fetchData(getFilterParams());
  };

  const openActionModal = (actionType: string, id: string, event) => {
    if (actionType == 'edit') {
      if (props.dataType == 'Notification') {
        openNotificationActionsModal(id, event);
      } else {
        openUserActionsModal(id, event);
      }
    } else if (actionType == 'viewParentComments') {
      emits('navigateToParent', id);
    } else {
      deleteComment(id, event);
    }
  };

  const setPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
      filterParams.value.page = page;
    }
  };

  const visiblePages = computed(() => {
    const pages: (number | string)[] = [];
    const current = currentPage.value;
    const total = totalPages.value;
    const delta = 2;

    if (total <= 1) return [1];

    pages.push(1);

    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(total - 1, current + delta);

    if (rangeStart > 2) {
      pages.push('...');
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < total - 1) {
      pages.push('...');
    }

    pages.push(total);

    return pages;
  });

  const fetchData = async (params: FilterParams = getFilterParams()) => {
    await tableConfigurations[props.dataType].fetchData(store, params);
  };

  // Fetch data when the component is mounted
  onMounted(() => {
    tableActionButtons();
    let params = JSON.parse(JSON.stringify(filterParams.value));

    params.searchParamsAND = [
      ...(tableConfigurations[props.dataType].initialFilters ?? []),
      ...(params.searchParamsAND || []),
    ];

    if (props.dataType === 'Notification') {
      columnConfiguration.value = [
        ...tableConfigurations[props.dataType].columns.filter(
          (column) =>
            column.title !== 'Scheduled' &&
            column.title !== 'Repeat' &&
            column.title !== 'Last Modified',
        ),
      ];
      params.searchParamsAND = [
        { searchTerm: 'sent', searchField: 'status', action: 'equals' },
      ];
    }

    fetchData(params);
  });

  const getProperty = (element, propName) => {
    if (!element || typeof element !== 'object') {
      return element;
    }
    return resolveProperty(element, propName.split('.'));
  };
  const resolveUpdatedAt = (obj: any): string => {
    var label = 'Last Modified: ';
    if (obj instanceof Resource) {
      if (obj.is_published) {
        label = 'Published: ';
      }
    }

    const date = new Date(obj.updated_at);
    const formattedDate = date.toISOString().split('T')[0].replace(/-/g, '/');

    return label + formattedDate;
  };

  const resolveDate = (date: Date): string => {
    if (!date) {
      return '';
    }
    const dateObj = new Date(date);
    const formattedDate = dateObj
      .toISOString()
      .split('T')[0]
      .replace(/-/g, '/');

    return formattedDate;
  };

  const resolveDateTime = (dateTime: Date): string => {
    const time = new Date(dateTime);
    const date = resolveDate(dateTime);
    return (
      date +
      ', ' +
      time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    );
  };

  const cutArray = (array, showMax) => {
    if (!showMax) {
      return array;
    }
    if (array.length > showMax) {
      const cutArray = array.slice(0, showMax - 1);
      cutArray.push(`+${array.length - showMax + 1}`);
      return cutArray;
    }
    return array;
  };

  const resolveProperty = (obj, propNameSplit): string => {
    if (propNameSplit.length > 1) {
      return resolveProperty(obj[propNameSplit[0]], propNameSplit.slice(1));
    } else {
      return obj[propNameSplit[0]];
    }
  };

  const openNotificationActionsModal = (notification_id, event) => {
    event.stopPropagation();
    notificationActionModal.value = true;
    selectedId.value = notification_id;
  };

  const handleNotificationAction = () => {
    notificationActionModal.value = false;
  };

  const openUserActionsModal = (user_id, event) => {
    event.stopPropagation();
    store.dispatch('user/selectUser', user_id);
    userActionModal.value = true;
    selectedId.value = user_id;
  };

  const handleUserAction = (action, banReason?: string) => {
    store
      .dispatch('user/' + action, {
        id: selectedId.value,
        filters: filterParams.value || {},
        ...(banReason && { ban_reason: banReason }),
      })
      .catch((error) => {
        alert(error.message);
      });
    userActionModal.value = false;
  };

  const settingsOnClick = (url, id) => {
    router.push({ path: url, query: { id } });
  };

  const emitSelectedOptions = () => {
    emits('update:selectedRows', selectedOptions.value);
  };

  const openResourcesModal = () => {
    showResourcesModal.value = true;
  };

  const deleteResources = () => {
    console.log('deleteResources');
  };

  const deleteSelected = () => {
    let payload = {
      data: selectedOptions.value,
    };
    if (
      (props.dataType === 'ConnectedResources' ||
        props.dataType === 'NotConnectedResources') &&
      filterParams.value.additionalParams
    ) {
      payload = {
        ...payload,
        [filterParams.value.additionalParams.name as string]: route.query.id,
      };
    }
    if (!tableConfigurations[props.dataType].storeActions.deleteAction) {
      console.error(
        'To use table delete functionality you have to provide deleteAction in storeActions for the table configuration.',
      );
    }
    store
      .dispatch(
        `${tableConfigurations[props.dataType].storeActions.deleteAction}`,
        payload,
      )
      .then(() => {
        fetchData(getFilterParams());
        selectedOptions.value = [];
      });
  };

  const handleClick = (buttonProps) => {
    const functionsMap = {
      deleteSelected,
      openResourcesModal,
      deleteResources,
      openDiscussionModal,
    };
    if (buttonProps.action != undefined) {
      const fnName = buttonProps.action;
      if (functionsMap[fnName]) {
        functionsMap[fnName]();
        if (fnName === 'openResourcesModal' && props.onActionClick) {
          props.onActionClick();
        }
      } else {
        console.error(`Function ${fnName} not found!`);
      }
    } else {
      router.push(buttonProps.route);
    }
  };

  const refreshSentNotifications = () => {
    statusBarSelection.value = {
      searchTerm: 'sent',
      searchField: 'status',
      action: 'equals',
    };

    fetchData(getFilterParams());
  };

  const handleRowClick = (row: any) => {
    openCommentsModal(props.dataType, row.discussion_id, true);
  };

  const shouldShowParentCommentIcon = (column: any, row: any) => {
    if (column.action !== 'viewParentComments') return true;

    if (props.type === 'parent_comment') {
      return row.parent_comment_id && row.parent_comment_id !== props.currentId;
    } else {
      return row.parent_comment_id;
    }
  };

  const getActionIcon = (action: string) => {
    return action === 'edit' || action === 'notification_action'
      ? 'threeDotsIcon'
      : 'deleteIcon';
  };

  const handleExportToCSV = async () => {
    try {
      const params = getFilterParams();
      // Set page size to maximum to export all results
      params.pageSize = 100000;

      const blob = await userService.exportToCSV(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `users-export-${date}.csv`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('CSV export error:', error);
    }
  };
</script>

<template>
  <ViewCommentsMolecule
    :show="showNoCommentsModal"
    @update:show="showNoCommentsModal = $event"
    :resource_id="selectedId"
    :type="selectedDataType"
    v-if="showNoCommentsModal"
  />
  <TabbarOrganism
    v-if="tableConfigurations[props.dataType].tabs"
    :buttons="tableConfigurations[props.dataType].tabs.fields"
    :countGetter="tableConfigurations[props.dataType].tabs.countGetter"
    :detailsButtonprops="actionButton"
    v-model="statusBarSelection"
    v-model:activeButton="activeButton"
    @buttonClick="handleClick"
    :filterField="tableConfigurations[props.dataType].tabs.field"
  />
  <SearchbarMolecule
    v-if="type !== 'parent_comment'"
    v-model="filterParams"
    :showFilter="
      tableConfigurations[props.dataType].filterSettings?.title ?? false
    "
    :filterTitle="
      tableConfigurations[props.dataType].filterSettings?.title ?? ''
    "
    :filterOptions="props.filterOptions"
    :additionalSortOptions="tableConfigurations[props.dataType].additionalSortOptions"
    :showExport="props.dataType === 'User'"
    @export="handleExportToCSV"
  />

  <NoDataMolecule
    v-if="!rowsData || rowsData.length == 0"
    :noDataButtonProps="noDataStateProps"
    icon="bookIcon"
    @buttonClick="handleClick"
  />
  <div v-else>
    <table>
      <thead>
        <tr>
          <th v-if="tableConfigurations[props.dataType].rowSelection"></th>
          <th v-for="column in columnConfiguration" :key="column.propertyName" :style="column.width ? { width: column.width } : {}">
            {{ column.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rowsData"
          :key="row.id"
          :class="{ 'clickable-row': props.dataType === 'GroupDiscussion' }"
          v-on="
            props.dataType === 'GroupDiscussion'
              ? { click: () => handleRowClick(row) }
              : {}
          "
        >
          <td v-if="tableConfigurations[props.dataType].rowSelection">
            <div class="flex-container">
              <!-- Wrap the checkbox input and custom checkbox in a label -->
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  class="table-row-checkbox-input"
                  :value="
                    row[
                      tableConfigurations[props.dataType].rowSelection
                        .rowSelectionProperty
                    ]
                  "
                  v-model="selectedOptions"
                  @change="emitSelectedOptions"
                  @click.stop
                />
                <span class="table-custom-checkbox" @click.stop></span>
                <span class="table-checkbox-label"></span>
              </label>
            </div>
          </td>
          <td v-for="column in columnConfiguration" :key="column.propertyName">
            <template v-if="column.settings">
              <IconAtom
                icon="settingIcon"
                @click="
                  settingsOnClick(
                    column.settingsUrl,
                    row[column.settingsIdProperty],
                  )
                "
              >
              </IconAtom>
            </template>
            <template v-if="column.action">
              <IconAtom
                v-if="shouldShowParentCommentIcon(column, row)"
                :icon="column.icon || getActionIcon(column.action)"
                class="three-dots-icon"
                @click.stop="
                  openActionModal(
                    column.action,
                    row[column.settingsIdProperty],
                    $event,
                  )
                "
              >
              </IconAtom>
              <UserActionModal
                v-if="
                  userActionModal &&
                  row[column.settingsIdProperty] == selectedId
                "
                @close="userActionModal = false"
                @action="handleUserAction"
                :is-visible="userActionModal"
              ></UserActionModal>
              <NotificationActionModal
                v-if="
                  notificationActionModal &&
                  row[column.settingsIdProperty] == selectedId
                "
                @close="notificationActionModal = false"
                @action="handleNotificationAction"
                @refresh="refreshSentNotifications()"
                :is-visible="notificationActionModal"
                :activeButton="activeButton"
                :notification-id="selectedId"
              >
              </NotificationActionModal>
            </template>
            <template v-if="column.actionName">
              <ButtonAtom
                class="btn btn-secondary"
                @click="column.actionHandler(row)"
              >
                {{ column.actionName }}
              </ButtonAtom>
            </template>
            <template v-else>
              <div
                class="flex-container"
                @click="
                  openCommentsModal(
                    props.dataType,
                    row[
                      tableConfigurations[props.dataType].rowSelection
                        ?.rowSelectionProperty
                    ],
                    column.title === 'Comments' ? true : false,
                  )
                "
              >
                <span v-if="column.imageProperty && row[column.imageProperty]">
                  <img
                    :class="column.userImage ? 'user-image' : 'row-image'"
                    :src="row[column.imageProperty]"
                  />
                </span>
                <span v-if="column.imageProperty && !row[column.imageProperty]">
                  <img
                    :class="column.userImage ? 'user-image' : 'row-image'"
                    :src="column.userImage ? userDefaultImage : defaultImage"
                  />
                </span>
                <template v-if="column.icon != null && !column.action">
                  <IconAtom :icon="column.icon"></IconAtom>
                </template>
                <!-- Handling Arrays -->
                <span
                  class="flex-container"
                  v-if="Array.isArray(row[column.propertyName])"
                >
                  <div
                    :class="[column.class, 'row-text']"
                    v-for="(element, index) in cutArray(
                      row[column.propertyName],
                      column.arrayShowMax,
                    )"
                    :key="index"
                  >
                    {{ getProperty(element, column.visibleProperty) }}
                  </div>
                </span>

                <!-- Handling Objects -->
                <div
                  class="flex-container"
                  v-else-if="typeof row[column.propertyName] === 'object'"
                >
                  <div
                    v-if="column.type && column.type == 'checkbox'"
                    :key="column.propertyName"
                  >
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        class="table-row-checkbox-input"
                        :checked="
                          row[column.propertyName]?.[column.visibleProperty]
                        "
                        :disabled="column.disabled"
                        @click.stop
                      />
                      <span class="table-custom-checkbox" @click.stop></span>
                    </label>
                  </div>
                  <div
                    v-else
                    :class="[column.class, 'row-text']"
                    :key="column.propertyName"
                  >
                    {{
                      getProperty(
                        row[column.propertyName],
                        column.visibleProperty,
                      )
                    }}
                  </div>
                </div>
                <div
                  :class="[column.class, 'row-text']"
                  v-else-if="column.propertyName == 'updated_at'"
                >
                  {{ resolveUpdatedAt(row) }}
                </div>
                <div
                  :class="[column.class, 'row-text']"
                  v-else-if="column.type && column.type == 'date'"
                >
                  {{ resolveDate(row[column.propertyName]) }}
                </div>
                <div
                  :class="[column.class, 'row-text']"
                  v-else-if="column.type && column.type == 'datetime'"
                >
                  {{ resolveDateTime(row[column.propertyName]) }}
                </div>
                <div v-else-if="column.propertyName == 'user_status'" class="status-cell">
                  <div
                    :class="{
                      'status-active-wrapper':
                        row[column.propertyName] == 'active',
                    }"
                  >
                    <span
                      class="status-text"
                      :class="{
                        'status-border': row[column.propertyName] != 'active',
                        'status-color': row[column.propertyName] == 'banned',
                      }"
                    >
                      <IconAtom
                        icon="banIcon"
                        class="status-color"
                        v-if="row[column.propertyName] == 'banned'"
                      />
                      {{ row[column.propertyName] }}</span
                    >
                  </div>
                  <div
                    v-if="row['user_status'] == 'banned' && row['ban_reason']"
                    class="ban-reason-tooltip"
                  >
                    <span class="ban-reason-label">Reason:</span> {{ row['ban_reason'] }}
                    <span v-if="row['banned_at']" class="ban-reason-date">
                      ({{ new Date(row['banned_at']).toLocaleDateString() }})
                    </span>
                  </div>
                </div>
                <div v-else-if="column.propertyName == 'is_premium'" class="row-text">
                  <span v-if="row['subscription_type'] === 'PURCHASE'" class="plan-badge plan-badge-premium">
                    Premium
                  </span>
                  <span v-else class="plan-badge plan-badge-free">
                    Free
                  </span>
                </div>
                <div v-else-if="column.propertyName == 'subscription_type'" class="row-text">
                  <span v-if="row[column.propertyName] == 'FREE_TRIAL'" class="sub-badge sub-badge-trial">
                    Trial
                  </span>
                  <span v-else-if="row[column.propertyName] == 'COUPON'" class="sub-badge sub-badge-coupon">
                    Promo Code
                  </span>
                  <span v-else-if="row[column.propertyName] == 'PURCHASE'" class="sub-badge sub-badge-paid">
                    Paid
                  </span>
                  <span v-else class="sub-badge sub-badge-none">
                    -
                  </span>
                </div>
                <div
                  :class="[column.class, 'row-text']"
                  v-else-if="
                    column.visibleOn &&
                    column.visibleOn == statusBarSelection?.searchTerm
                  "
                >
                  {{ row[column.propertyName] }}
                </div>
                <div
                  :class="[column.class, 'row-text']"
                  v-else-if="column.type == 'checkbox'"
                >
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      class="table-row-checkbox-input"
                      :checked="row[column.propertyName]"
                      :disabled="column.disabled"
                      @click.stop
                    />
                    <span class="table-custom-checkbox" @click.stop></span>
                  </label>
                </div>
                <div :class="[column.class, 'row-text']" v-else>
                  {{ row[column.propertyName] }}
                </div>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="pagination">
      <button @click="setPage(currentPage - 1)" :disabled="currentPage === 1">
        &laquo;
      </button>
      <button
        v-for="page in visiblePages"
        :key="page"
        @click="typeof page === 'number' && setPage(page)"
        :disabled="currentPage === page || typeof page !== 'number'"
        :class="{ active: currentPage === page }"
      >
        {{ page }}
      </button>
      <button
        @click="setPage(currentPage + 1)"
        :disabled="currentPage >= totalPages"
      >
        &raquo;
      </button>
    </div>
  </div>
</template>

<style scoped src="./TableMolecule.css"></style>
